import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import { RevealScope, useReveal } from "../Motion";

// Every glass card rises into view as it scrolls in (see components/Motion);
// pass `reveal={false}` for one that must be visible immediately. Cards inside
// something that already reveals (a timeline row) arrive with it instead.
const GlassCard = ({
  children,
  sx = {},
  onClick,
  href,
  to,
  accentColor,
  hoverEffect = true,
  reveal: animate = true,
  ...props
}) => {
  const reveal = useReveal({ disabled: !animate });

  const baseStyles = {
    // No backdrop-filter. These cards sit on the smooth brand gradient, and a
    // blurred smooth gradient looks exactly like the gradient - but each card's
    // blur was a GPU pass re-run on every frame the backdrop moved, which with
    // ~40 cards across About, Resume, Contact and the case studies was a large
    // share of the scroll jank. The tint alone carries the glass look.
    background: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: "12px",
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
    position: "relative",
    ...(hoverEffect && {
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.25)",
        border: "1px solid rgba(255, 255, 255, 0.25)",
      },
    }),
    ...(accentColor && {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: accentColor,
      },
    }),
    ...sx,
  };

  const content = <RevealScope active={reveal.active}>{children}</RevealScope>;
  const shared = { ref: reveal.ref, ...reveal.props, onClick, ...props };

  if (to) {
    return (
      <Box
        component={Link}
        to={to}
        sx={{
          ...baseStyles,
          textDecoration: "none",
          cursor: "pointer",
          display: "block",
        }}
        {...shared}
      >
        {content}
      </Box>
    );
  }

  if (href) {
    return (
      <Box
        component="a"
        href={href}
        sx={{
          ...baseStyles,
          textDecoration: "none",
          cursor: "pointer",
          display: "block",
        }}
        {...shared}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={baseStyles}
      style={onClick ? { cursor: "pointer" } : undefined}
      {...shared}
    >
      {content}
    </Box>
  );
};

export default GlassCard;
